/**
 * Backend rbac role vocabulary.
 *
 * USER is the logged-in signal, spelled once in `isIdentifiedUser` below and
 * shared by `useUser`, `prepareAppState`, `useTranslation`, the GTM identity
 * payload and the intercom BFF route's post-fetch profile check.
 *
 * GUEST is the anonymous-session role, verified 2026-10-07 (PRO-782) by
 * minting a session against liga1/production: the backend emits 'Guest' in both
 * the access token's `User.role` claim and the profile's `rbac.role`. It was
 * previously spelled ANONYMOUS: 'Anonymous', a value nothing emits — key and
 * value were corrected together so the name cannot drift from it again. Its one
 * production consumer is the intercom BFF route's bearer gate, which reads the
 * token claim; if claim and `rbac.role` ever diverge, this has to split in two.
 * The role fields below stay open to unknown strings: the backend owns the
 * vocabulary.
 *
 * Lives in its own module (not the `@/constants` barrel) so BFF routes and
 * SSR code can import it without dragging the barrel's heavyweight tables
 * (countries, breakpoints) into their module graphs. The barrel re-exports it
 * for existing client imports.
 */
export const USER_ROLES = {
  USER: 'User',
  GUEST: 'Guest',
} as const

/**
 * Known role literals plus any backend-emitted string: the backend owns the
 * role vocabulary (Admin, CMS roles, the real anonymous value), so narrowing
 * to the known set would make the type lie. `string & {}` keeps literal
 * autocomplete without closing the set.
 */
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES] | (string & {})

/**
 * The single logged-in test: a backend profile whose rbac role is USER.
 *
 * Takes the role, not a profile: analytics callers hold an already-flattened
 * `UserRole | null` with no profile to wrap, so a structural-profile parameter
 * would force a throwaway `{ rbac: { role } }` adapter at those sites.
 *
 * Deliberately loose: callers that also need an id (the intercom BFF route
 * signs `profile._id`) answer that separately, at the call site.
 */
export const isIdentifiedUser = (role?: UserRole | null): boolean => role === USER_ROLES.USER
